import { CANCEL_PASSPORT_TEMPLATE } from "@/pages/postloss/estate-essentials/templates/cancel-passport";
import { VOTER_REGISTRATION_TEMPLATE } from "@/pages/postloss/estate-essentials/templates/voter-registration";
import {
  MAIL_FORWARDING_AUTHORITY_TEMPLATE,
  MAIL_FORWARDING_TEMPLATE,
} from "@/pages/postloss/estate-essentials/templates/mail-forwarding";
import { SOCIAL_SECURITY_TEMPLATE } from "@/pages/postloss/estate-essentials/templates/social-security";
import { VETERANS_BENEFITS_TEMPLATE } from "@/pages/postloss/estate-essentials/templates/veterans-benefits";
import {
  VEHICLE_TITLE_TRANSFER_AUTHORITY_TEMPLATE,
  VEHICLE_TITLE_TRANSFER_TEMPLATE,
} from "@/pages/postloss/estate-essentials/templates/vehicle-boat-titles";

import type { LegalRole } from "@/shared/hooks/useUserEstateRole";
import type { EssentialModuleId } from "@/shared/types/estateEssentials";

/**
 * Maps the granular legal role to the template key used by the ops team.
 * - executor / administrator both use the "lot" (Letters of Testamentary) template
 * - trustee and sea map directly
 */
function getTemplateRole(legalRole: LegalRole): string | null {
  if (legalRole === "executor" || legalRole === "administrator") return "lot";
  if (legalRole === "trustee") return "trustee";
  return null;
}

/**
 * Per-state templates for modules that need entirely different content per state.
 * Populated as ops authors the templates.
 *
 * Structure: moduleId -> stateCode -> template string
 */
const STATE_TEMPLATES: Partial<Record<EssentialModuleId, Record<string, string>>> = {};

/**
 * Role-specific templates for modules that need entirely different content
 * based on the user's legal role (lot, trustee).
 *
 * Structure: moduleId -> templateRole -> template string
 */
const ROLE_TEMPLATES: Partial<Record<EssentialModuleId, Partial<Record<string, string>>>> = {
  "mail-forwarding": {
    lot: MAIL_FORWARDING_AUTHORITY_TEMPLATE,
    trustee: MAIL_FORWARDING_AUTHORITY_TEMPLATE,
  },
  "vehicle-boat-titles": {
    lot: VEHICLE_TITLE_TRANSFER_AUTHORITY_TEMPLATE,
    trustee: VEHICLE_TITLE_TRANSFER_AUTHORITY_TEMPLATE,
  },
};

/**
 * Default (generic) templates per module.
 * Used when no role-specific or state-specific template exists.
 */
const DEFAULT_TEMPLATES: Partial<Record<EssentialModuleId, string>> = {
  "cancel-passport": CANCEL_PASSPORT_TEMPLATE,
  "voter-registration": VOTER_REGISTRATION_TEMPLATE,
  "mail-forwarding": MAIL_FORWARDING_TEMPLATE,
  "social-security-benefits": SOCIAL_SECURITY_TEMPLATE,
  "veterans-survivor-benefits": VETERANS_BENEFITS_TEMPLATE,
  "vehicle-boat-titles": VEHICLE_TITLE_TRANSFER_TEMPLATE,
};

/**
 * Resolves the guide template for a module using a waterfall:
 * 1. Role-specific template (e.g. lot, trustee)
 * 2. State-specific template
 * 3. Default template
 *
 * Returns null if no template exists for the module.
 */
export function resolveGuideTemplate(
  moduleId: EssentialModuleId,
  stateCode: string,
  legalRole?: LegalRole,
): string | null {
  const templateRole = legalRole ? getTemplateRole(legalRole) : null;

  if (templateRole) {
    const roleTemplate = ROLE_TEMPLATES[moduleId]?.[templateRole];
    if (roleTemplate) return roleTemplate;
  }

  const stateTemplate = stateCode ? STATE_TEMPLATES[moduleId]?.[stateCode] : undefined;
  if (stateTemplate) return stateTemplate;

  return DEFAULT_TEMPLATES[moduleId] ?? null;
}
